import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * 채용 카드 칩 한 줄 — 칸에 들어가는 만큼만 보이고 나머지는 "+N"(2026-09-30 카드 높이 고정 결정).
 * 접는 순서(docs/recruitment-alba-improve-plan.md 3-1): ① 뒤쪽 칩(복지)부터 +N 으로 ② 첫 칩(역)을 말줄임(최소 [FIRST_MIN_PX])
 * ③ 그래도 넘치면 첫 칩도 +N 으로. [pinned] 칩(급여·당일 지급)은 끝까지 남기고, 그 칩들만으로 꽉 차면 "+N"은 빼 잘린 글자가 보이지 않게 한다.
 * 태그 폭은 글이 바뀔 때 한 번만 재고, 칸 폭이 바뀌면 잰 폭으로 다시 계산만 한다(스크롤 중 재측정 없음).
 */

/** "+N" 자리 — 12px 글 두세 자 + 여백 */
const PLUS_RESERVE_PX: number = 26;
/** 첫 칩(역)을 줄일 수 있는 최소 폭 — 이보다 좁아야 하면 +N 으로 접는다 */
const FIRST_MIN_PX: number = 56;

export interface OneLineTagsProps {
    tags: string[];
    /** 처음부터 이 개수까지만 후보로(나머지는 +N) */
    max?: number;
    /** 첫 칩(역)을 말줄임으로 남긴다 */
    keepFirst?: boolean;
    /** 끝까지 남기는 칩 글(급여·당일 지급) */
    pinned?: string[];
    gapPx: number;
    heightPx?: number;
    tagStyle: React.CSSProperties;
    /** 이 글의 칩만 [accentStyle] 을 덧입힌다(예: 당일 지급) */
    accentTag?: string;
    accentStyle?: React.CSSProperties;
    plusStyle: React.CSSProperties;
    style?: React.CSSProperties;
    /** 첫 줄에서 접힌 이 칩들만 다음 줄로 옮긴다. 모든 칩을 옮길 수 있으면 원래 순서대로 줄을 나눈다. */
    overflowTags?: string[];
}

interface Layout {
    /** 앞에서부터 이 개수까지 후보(첫 칩을 접어도 개수는 그대로 센다) */
    count: number;
    /** 첫 칩을 +N 으로 접었는지 */
    dropFirst: boolean;
    /** 첫 칩 최대 폭(px) — 말줄임할 때만 */
    firstMax: number | null;
    /** +N 을 그리지 않음(남긴 칩만으로 꽉 참) */
    hidePlus: boolean;
    /** 남긴 칩만으로도 넘치면 마지막 칩 최대 폭(px) — 끝을 잘라 버리지 않고 말줄임 */
    lastMax: number | null;
}

const OneLineTags = ({ tags, max, keepFirst = false, pinned, gapPx, heightPx = 22, tagStyle, accentTag, accentStyle, plusStyle, style, overflowTags }: OneLineTagsProps) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const widthsRef = useRef<number[] | null>(null);
    const candidates: string[] = max !== undefined ? tags.slice(0, max) : tags;
    const preserveOrder: boolean = !!overflowTags?.length && tags.every(tag => overflowTags.includes(tag));
    const key: string = tags.join('\u0001') + '\u0002' + (pinned ?? []).join('\u0001') + '\u0002' + preserveOrder;
    const [layout, setLayout] = useState<Layout>({ count: candidates.length, dropFirst: false, firstMax: null, hidePlus: false, lastMax: null });
    const [measuredKey, setMeasuredKey] = useState<string>('');

    useLayoutEffect(() => {
        const box: HTMLDivElement | null = boxRef.current;
        if (!box) return undefined;
        const compute = (): void => {
            const widths: number[] | null = widthsRef.current;
            const available: number = box.clientWidth;
            if (!widths || available === 0) return;
            const pinnedIndex: number = candidates.reduce((last: number, tag: string, i: number) => ((pinned ?? []).includes(tag) ? i : last), -1);
            const hasFirst: boolean = keepFirst && candidates.length > 0;
            const minCount: number = Math.max(pinnedIndex + 1, hasFirst ? 1 : 0);
            /** 앞에서 [count] 개(첫 칩 폭 [firstWidth], 0 이면 접음)를 그릴 때 필요한 폭 */
            const need = (count: number, firstWidth: number | null, plus: boolean): number => {
                const shown: number[] = widths.slice(0, count).map((w: number, i: number) => (i === 0 && firstWidth !== null ? firstWidth : w)).filter((w: number) => w > 0);
                const sum: number = shown.reduce((a: number, b: number) => a + b, 0) + Math.max(0, shown.length - 1) * gapPx;
                return sum + (plus ? (shown.length > 0 ? gapPx : 0) + PLUS_RESERVE_PX : 0);
            };
            // 두 줄로 펼칠 때는 역부터 원래 순서대로 채운다. 접힌 칩과 +N은 다음 줄이 맡는다.
            if (preserveOrder) {
                for (let count = candidates.length; count >= 1; count -= 1) {
                    if (need(count, null, false) <= available) {
                        setLayout({ count, dropFirst: false, firstMax: null, hidePlus: false, lastMax: null });
                        return;
                    }
                }
                setLayout({ count: 1, dropFirst: false, firstMax: hasFirst ? available : null, hidePlus: false, lastMax: hasFirst ? null : available });
                return;
            }
            // ① 뒤에서부터 접기 — 남겨야 하는 칩 앞까지
            for (let count = candidates.length; count >= minCount; count -= 1) {
                const hidden: boolean = tags.length - count > 0;
                if (need(count, null, hidden) <= available) {
                    setLayout({ count, dropFirst: false, firstMax: null, hidePlus: false, lastMax: null });
                    return;
                }
            }
            const hidden: boolean = tags.length - minCount > 0;
            // 첫 칩을 뺀 나머지(남길 칩 + "+N")가 차지하는 폭 → 첫 칩에 남는 폭
            const others: number = need(minCount, 0, hidden);
            const room: number = Math.floor(available - others - (others > 0 ? gapPx : 0));
            // ② 첫 칩(역) 말줄임
            if (hasFirst && room >= Math.min(FIRST_MIN_PX, widths[0])) {
                setLayout({ count: minCount, dropFirst: false, firstMax: room, hidePlus: false, lastMax: null });
                return;
            }
            // ③ 첫 칩도 접기(남길 칩이 있을 때만 — 없으면 예전처럼 좁게라도 남긴다). 남긴 칩만으로도 넘치면 +N 은 뺀다
            const dropFirst: boolean = hasFirst && pinnedIndex >= 1;
            const fits: boolean = need(minCount, dropFirst ? 0 : null, true) <= available;
            // 남긴 칩만으로도 넘치면(좁은 폭의 "당일 지급 + 시급 12,000~25,000원") 마지막 칩을 말줄임 — 잘린 글자가 보이지 않게
            const allPinned: number = need(minCount, dropFirst ? 0 : null, false);
            const beforeLast: number = need(minCount - 1, dropFirst ? 0 : null, false);
            const lastMax: number | null = !fits && allPinned > available && minCount > (dropFirst ? 1 : 0)
                ? Math.max(0, Math.floor(available - beforeLast - (beforeLast > 0 ? gapPx : 0)))
                : null;
            setLayout({ count: minCount, dropFirst, firstMax: hasFirst && !dropFirst ? Math.max(0, room) : null, hidePlus: !fits, lastMax });
        };
        /** 글이 바뀐 직후(전부 그린 상태)에 태그 폭을 한 번 잰다 — 칸이 숨겨져 있으면(폭 0) 보일 때까지 미룬다 */
        const measureIfNeeded = (): void => {
            if (measuredKey === key || box.clientWidth === 0) return;
            const chips: HTMLElement[] = Array.from(box.querySelectorAll<HTMLElement>('[data-tag]'));
            widthsRef.current = chips.map((chip: HTMLElement) => chip.getBoundingClientRect().width);
            setMeasuredKey(key);
        };
        measureIfNeeded();
        compute();
        if (typeof ResizeObserver === 'undefined') return undefined;
        let lastWidth: number = box.clientWidth;
        const ro: ResizeObserver = new ResizeObserver(() => {
            if (box.clientWidth === lastWidth) return;
            lastWidth = box.clientWidth;
            measureIfNeeded();
            compute();
        });
        ro.observe(box);
        return () => ro.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, measuredKey, gapPx, keepFirst]);

    const measured: boolean = measuredKey === key;
    const { count, dropFirst, firstMax, hidePlus, lastMax } = measured ? layout : { count: candidates.length, dropFirst: false, firstMax: null, hidePlus: false, lastMax: null };
    const start: number = dropFirst ? 1 : 0;
    const shown: string[] = candidates.slice(start, count);
    const extra: string[] = measured && !hidePlus && overflowTags?.length
        ? tags.filter((tag, i) => (i < start || i >= count) && overflowTags.includes(tag))
        : [];
    const hidden: number = tags.length - shown.length - extra.length;
    return (
        <>
        <div ref={boxRef} style={{ display: 'flex', flexWrap: 'nowrap', gap: `${gapPx}px`, overflow: 'hidden', height: `${heightPx}px`, alignItems: 'center', ...style }}>
            {shown.map((tag: string, i: number) => {
                const isFirst: boolean = keepFirst && start === 0 && i === 0;
                const isClippedLast: boolean = lastMax !== null && i === shown.length - 1;
                return (
                    <span
                        key={start + i}
                        data-tag
                        data-accent={accentTag !== undefined && tag === accentTag ? '' : undefined}
                        style={{
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            ...tagStyle,
                            ...(accentTag !== undefined && tag === accentTag ? accentStyle : {}),
                            ...(isFirst ? { maxWidth: firstMax !== null ? `${firstMax}px` : '100%', overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box' as const } : {}),
                            ...(isClippedLast ? { maxWidth: `${lastMax}px`, overflow: 'hidden', textOverflow: 'ellipsis', boxSizing: 'border-box' as const } : {}),
                        }}
                    >
                        {tag}
                    </span>
                );
            })}
            {hidden > 0 && !hidePlus && (
                <span data-plus style={{ whiteSpace: 'nowrap', flexShrink: 0, ...plusStyle }}>+{hidden}</span>
            )}
        </div>
        {extra.length > 0 && (
            <div data-card-part="welfare-overflow" style={{ height: heightPx, minWidth: 0, flexShrink: 0 }}>
                <OneLineTags
                    tags={extra}
                    max={max}
                    gapPx={gapPx}
                    heightPx={heightPx}
                    tagStyle={tagStyle}
                    plusStyle={plusStyle}
                    pinned={pinned}
                    accentTag={accentTag}
                    accentStyle={accentStyle}
                />
            </div>
        )}
        </>
    );
};

export default OneLineTags;
